import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Glyph } from "../../components/ui";
import { iconChipStyle } from "../../lib/icons-resolve";
import { Button, Caption, DescrBox, DifficultyTag, Icon, Lead, List, ListRow, SectionHeader, Sheet, Tag, Toggle } from "../../components/ui/ds";
import "./cleanup-sheet.css";

// v30 — sheet k postrehu „habit_overload" (priveľa návykov, nízka úspešnosť). Na rozdiel od
// ostatných postrehov sa tento NEDÁ opraviť jedným ťuknutím: ktorý z dvanástich návykov
// pustiť, vie len používateľ.
//
// Preto hromadný výber, nie akcie po riadkoch: pri prehltení sa púšťa viac vecí naraz a dve
// textové tlačidlá v každom riadku sa na 360 px ani nezmestia (názov návyku sa zlomil do
// jedného písmena na riadok). Vyberie sa čo pustiť, operácia sa zvolí raz dole.
//
// Archív je vratný, mazanie nie — preto je archív hlavné tlačidlo a mazanie si pýta potvrdenie.
// Výzvy sa nearchivujú ani nemažú, z tých sa dá len ODÍSŤ: sú záväzkom voči iným ľuďom.

export function CleanupSheet({ onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState([]);
  const [confirmDel, setConfirmDel] = useState(false);
  const [leaving, setLeaving] = useState(null);
  const [done, setDone] = useState(0);

  const load = () => api("/api/insight/cleanup").then(setD)
    .catch(() => setErr("Zoznam sa nepodarilo načítať."));
  useEffect(() => { load(); }, []);

  const toggle = (id) =>
    setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const run = async (fn, n) => {
    setBusy(true);
    setErr("");
    let ok = 0;
    try {
      ok = (await fn()) ?? n;
    } catch (e) {
      setErr((e && e.message) || "Nepodarilo sa.");
    }
    // POZOR: zoznam aj výber sa prečistia AJ po chybe. Dávka je čiastočne vykonaná, takže
    // stará `sel` by ukazovala na už zmazané návyky a ďalší pokus by padol na tom istom
    // mieste donekonečna. `done` počíta, čo sa naozaj podarilo.
    setDone((x) => x + ok);
    setSel([]);
    await load().catch(() => {});
    if (ok) onChanged && onChanged();
    setBusy(false);
    setConfirmDel(false);
    setLeaving(null);
  };

  // sekvenčne, nie paralelne — pri chybe je zrejmé, kde sa to zastavilo, a server
  // nedostane naraz tucet zápisov do tej istej tabuľky. Vracia počet skutočne
  // spracovaných, aby sa pri páde uprostred nezapočítala celá dávka.
  const applyAll = (suffix) => async () => {
    let n = 0;
    for (const id of sel) {
      await api(`/api/tasks/${id}${suffix}`, { method: "DELETE" });
      n += 1;
    }
    return n;
  };

  const sk = (n, one, few, many) => (n === 1 ? one : n >= 2 && n <= 4 ? few : many);
  const count = sel.length;

  return (
    <Sheet icon="folder" title="Pretriediť návyky" onClose={onClose}
      footer={count === 0 ? (
        <Button variant={done > 0 ? "primary" : "ghost"} block onClick={onClose}>
          {done > 0 ? `Hotovo — ${done} ${sk(done, "pustený", "pustené", "pustených")}` : "Zavrieť"}
        </Button>
      ) : confirmDel ? (
        <>
          <Caption variant="note">Zmazané sa už nedajú vrátiť. Archív áno.</Caption>
          <div className="bz-cleanup__foot">
            <Button variant="ghost" block disabled={busy} onClick={() => setConfirmDel(false)}>Späť</Button>
            <Button variant="destructive" block disabled={busy}
              onClick={() => run(applyAll(""))}>Naozaj zmazať {count}</Button>
          </div>
        </>
      ) : (
        <div className="bz-cleanup__foot">
          <Button variant="ghost" block disabled={busy} onClick={() => setConfirmDel(true)}>Zmazať</Button>
          <Button variant="primary" block disabled={busy}
            onClick={() => run(applyAll("?archive=1"))}>Archivovať {count}</Button>
        </div>
      )}>
      {err && <div className="bz-cleanup__err">{err}</div>}
      {!d && !err && <Caption variant="note">Načítavam…</Caption>}

      {d && (
        <>
          <DescrBox className="bz-cleanup__intro" text={
            "Menej návykov zvládnutých poriadne je viac než veľa načatých. Hore sú tie, "
            + "ktoré ti idú najhoršie — označ, čo chceš pustiť."} />

          <SectionHeader label="Návyky" count={d.habits.length} />
          {d.habits.length === 0 ? (
            <Caption variant="note">Žiadne návyky na prebranie.</Caption>
          ) : (
            <List className="bz-cleanup__list">
              {d.habits.map((t) => (
                <ListRow
                  key={t.id}
                  lead={<Lead size="sm" tone="outline" style={iconChipStyle(t.icon_color)}><Glyph icon={t.icon} size={17} /></Lead>}
                  title={t.label}
                  meta={
                    <span className="bz-cleanup__meta">
                      <DifficultyTag level={t.difficulty} size="xs" showLabel={false} />
                      {t.rate != null && (
                        <Tag tone={t.rate < 40 ? "danger" : "neutral"} size="xs">{t.rate} % za mesiac</Tag>
                      )}
                    </span>
                  }
                  trailing={<Toggle on={sel.includes(t.id)} onChange={() => toggle(t.id)}
                    aria-label={`Pustiť návyk ${t.label}`} />}
                />
              ))}
            </List>
          )}

          {d.challenges.length > 0 && (
            <>
              <SectionHeader label="Výzvy a súťaže" count={d.challenges.length} />
              <Caption variant="note">Tieto sa nearchivujú — sú záväzkom voči ostatným, dá sa z nich len odísť.</Caption>
              <List className="bz-cleanup__list">
                {d.challenges.map((c) => (
                  <ListRow
                    key={c.id}
                    lead={<Lead size="sm" tone="outline"><Icon name={c.is_competition ? "trophy" : "flag"} size={16} color="currentColor" /></Lead>}
                    title={c.name}
                    meta={c.habit}
                    trailing={
                      leaving === c.id ? (
                        <span className="bz-cleanup__confirm">
                          <Button variant="ghost" size="sm" onClick={() => setLeaving(null)}>Späť</Button>
                          <Button variant="destructive" size="sm" disabled={busy}
                            onClick={() => run(() => api(`/api/challenges/${c.id}/leave`, { method: "DELETE" }), 1)}>
                            Naozaj odísť
                          </Button>
                        </span>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => setLeaving(c.id)}>Odísť</Button>
                      )
                    }
                  />
                ))}
              </List>
            </>
          )}
        </>
      )}
    </Sheet>
  );
}
